import { v4 as uuidv4 } from 'uuid';

import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { createKindResultDecisionSelectOption } from 'CorePackage/CatalogsModule/common/createKindResultDecisionSelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { createResponsibilityTypeSelectOption } from 'CorePackage/CatalogsModule/common/createResponsibilityTypeSelectOption';
import { createKnmResponsibleTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmResponsibleTypeSelectOption';
import { createKnmNotificationMethodSelectOption } from 'CorePackage/CatalogsModule/common/createKnmNotificationMethodSelectOption';
import { convertSubjectDataToFormValues } from 'KnmPackage/KnmSubjectModule/common/convertSubjectDataToformValues';
import { convertSubjectType } from 'KnmPackage/KnmSubjectModule/common/convertSubjectType';
import { normalizeProductsDescription } from 'CorePackage/MainModule/common/parseProductsDescription';

import { IKnmErknmForm } from '../interfaces/IKnmErknmForm';
import { IKnmErknmFormResultDecision } from '../interfaces/IKnmErknmFormResultDecision';
import { IKnmErknmFormInspector } from '../interfaces/IKnmErknmFormInspector';
import { IKnmErknmFormSubject } from '../interfaces/IKnmErknmFormSubject';
import { createOrganizationTemplateSelectOption } from './createOrganizationTemplateSelectOption';
import { getSelectOption } from './getSelectOption';
import { convertKnmDataViolatedRequirementsToFormValues } from './convertKnmDataViolatedRequirementsToFormValues';
import { createPunishmentNatureSelectOption } from '../../../CorePackage/CatalogsModule/common/createPunishmentNatureSelectOption';
import { IKnmLink } from '../../../APIPackage/KnmModule/interfaces/IKnmLink';
import { fetchKnmResultDecisionLinks } from '../../../APIPackage/KnmModule/services/knmAdditionalDataApi';
import { IKnmErknmFormAct } from '../interfaces/IKnmErknmFormAct';
import { generateOrganizationTemplateText } from './generateOrganizationTemplateText';
import { IErknmResultDecisionActSelectOption } from '../interfaces/IErknmResultDecisionActSelectOption';
import { IKnmActFamiliarizationNotificationsForm } from '../interfaces/IKnmActFamiliarizationNotificationsForm';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { KnmResultDecisionSubjectTypesEnum } from '../../../KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';
import { convertSignersToFormValues } from './convertSignersToFormValues';
import { sellersOptions } from '../../ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ErknmResultDecisionsSellersDecision/ErknmResultDecisionsSellersDecisionContent';

interface IConvertKnmDataResultDecisionsToFormValuesGeneratorParams {
  knmId: number | undefined;
  state: IRootState;
  resultDecisions: IKnmSubject248['resultDecisions'] | undefined;
  resultDecisionsOrganization: IKnmErknmFormSubject;
  formActs: IKnmErknmFormAct[] | undefined;
}

export const convertKnmDataResultDecisionsToFormValues = async ({
  knmId,
  state,
  resultDecisions,
  resultDecisionsOrganization,
  formActs,
}: IConvertKnmDataResultDecisionsToFormValuesGeneratorParams): Promise<IKnmErknmForm['resultDecisions']> => {
  const promises = resultDecisions?.map(async item => {
    let links: IKnmLink[] | undefined;

    if (item.links && knmId) {
      links = await fetchKnmResultDecisionLinks(knmId);
    }
    const titleSignerSelectOption = item.titleSigner?.dictId
      ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
          state,
          item.titleSigner?.dictId,
          item.titleSigner?.dictVersionId,
        )
      : undefined;

    let act: IErknmResultDecisionActSelectOption | undefined;
    const selectedFormAct = formActs?.find(formAct => formAct.guid === item.actGuid);

    if (selectedFormAct) {
      act = {
        id: selectedFormAct.guid || '',
        value: selectedFormAct.guid || '',
        label: `${selectedFormAct.numberAct} - ${generateOrganizationTemplateText(
          selectedFormAct.organization?.inn || undefined,
          selectedFormAct.organization?.ogrn || undefined,
          selectedFormAct.organization?.organizationName || undefined,
          selectedFormAct.organization?.rafpNza || undefined,
        )}`,
      };
    }

    const formResultDecision: IKnmErknmFormResultDecision = {
      status: item.status || undefined,
      agreementStatus: item.agreementStatus || undefined,
      links,
      founder: convertSubjectDataToFormValues(item.founder, true),
      // Присваиваем значение по-умолчанию в случае, если Есть организация, а поле приехало пустое
      subjectTypeFounder: convertSubjectType(
        item.subjectType ??
          (resultDecisionsOrganization ? KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT : null),
      ),
      kindDecision: item.kindDecision ? createKindResultDecisionSelectOption(item.kindDecision) : undefined,
      punishmentNature: item.punishmentNature ? createPunishmentNatureSelectOption(item.punishmentNature) : undefined,
      document: item.document
        ? {
            active: item.document.active || undefined,
            attachments: item.document.attachments || undefined,
            correlationToken: item.document.correlationToken || undefined,
            description: item.document.description || undefined,
            guid: item.document.guid || undefined,
            type: item.document.type || undefined,
          }
        : undefined,
      protocol: item.protocol
        ? {
            active: item.protocol.active || undefined,
            attachments: item.protocol.attachments || undefined,
            correlationToken: item.protocol.correlationToken || undefined,
            description: item.protocol.description || undefined,
            guid: item.protocol.guid || undefined,
            type: item.protocol.type || undefined,
          }
        : undefined,
      noteLawsuits: item.noteLawsuits?.map(noteLawsuit => ({
        value: noteLawsuit.value || undefined,
        valueReactQuillEditor: handleReactQuillDeltaValue(noteLawsuit.valueReactQuillEditor || noteLawsuit.value),
        guid: noteLawsuit.guid || undefined,
        dateCreate: noteLawsuit.dateCreate || null,
        listId: noteLawsuit.guid || uuidv4(),
      })),
      injunction:
        (item.injunction && {
          dateResolved: convertInitialKnmDateToFormDate(item.injunction.dateResolved, DATE_FORMAT_LONG_FNS),
          note: item.injunction.note || undefined,
          noteReactQuillEditor: handleReactQuillDeltaValue(
            item.injunction.noteReactQuillEditor || item.injunction.note,
          ),
          guid: item.injunction.guid || undefined,
          dateCreate: item.injunction.dateCreate || undefined,
        }) ||
        undefined,
      isProsecution:
        booleanSelectOptions.find((option: IBooleanSelectOption) => option.value === item.isProsecution) || undefined,
      subjectDecision: item.subjectDecision
        ? {
            inn: item.subjectDecision.inn || undefined,
            ogrn: item.subjectDecision.ogrn || undefined,
            organizationName: item.subjectDecision.organizationName || undefined,
            guid: item.subjectDecision.guid || undefined,
            dateCreate: item.subjectDecision.dateCreate || undefined,
          }
        : undefined,
      subjectIsAutocomplete: typeof item.subjectIsAutocomplete === 'boolean' ? item.subjectIsAutocomplete : undefined,
      responsibleEntities:
        item.responsibleEntities?.map(responsibleEntity => {
          const knmResponsibleTypesSelectOption = responsibleEntity.subjectType
            ? createKnmResponsibleTypeSelectOption(responsibleEntity.subjectType)
            : undefined;
          const responsibilityTypeSelectOption = responsibleEntity.responsibilityType?.code
            ? createResponsibilityTypeSelectOption(responsibleEntity.responsibilityType)
            : undefined;

          return {
            subjectType: knmResponsibleTypesSelectOption,
            inn: responsibleEntity.inn || undefined,
            ogrn: responsibleEntity.ogrn || undefined,
            snils: responsibleEntity.snils || undefined,
            organizationName: responsibleEntity.organizationName || undefined,
            organization: undefined,
            fullName: responsibleEntity.fullName || undefined,
            position: responsibleEntity.position || undefined,
            listId: responsibleEntity.guid || uuidv4(),
            guid: responsibleEntity.guid || undefined,
            ervkControlKindNpaUnit: responsibleEntity.ervkControlKindNpaUnit || null,
            responsibilityType: responsibilityTypeSelectOption,
            punishmentAmount:
              typeof responsibleEntity.punishmentAmount === 'number'
                ? responsibleEntity.punishmentAmount.toString()
                : undefined,
            dateCreate: null,
            structuresNPA: responsibleEntity.structuresNPA?.map(structureNPA => ({
              value: structureNPA.value || undefined,
              guid: structureNPA.guid || undefined,
              listId: structureNPA.guid || uuidv4(),
              dateCreate: structureNPA.dateCreate || null,
            })),
            punishmentAmountMeasure: responsibleEntity.punishmentAmountMeasure || undefined,
            punishmentTerm: responsibleEntity.punishmentTerm || undefined,
            punishmentNote: responsibleEntity.punishmentNote || undefined,
            punishmentUins: responsibleEntity.punishmentUins || undefined,
            inspectionResults: responsibleEntity.inspectionResults || undefined,
          };
        }) || undefined,
      numberDecision: item.numberDecision || undefined,
      dateDecision: convertInitialKnmDateToFormDate(item.dateDecision, DATE_FORMAT_LONG_FNS, DATE_FORMAT_ISO_FNS),
      fioSigner: item.fioSigner || undefined,
      titleSigner: titleSignerSelectOption,
      inspectors: item.inspectors?.map(inspector => {
        const selectOption = inspector.position?.dictId
          ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
              state,
              inspector.position?.dictId,
              inspector.position?.dictVersionId,
            )
          : undefined;
        const formInspector: IKnmErknmFormInspector = {
          ...inspector,
          position: selectOption,
          listId: inspector.guid || uuidv4(),
          label: `${inspector.fullName} - ${selectOption?.label}`,
        };

        return formInspector;
      }),
      guid: item.guid || undefined,
      dateCreate: item.dateCreate || undefined,
      dateResolvedByAgreement: convertInitialKnmDateToFormDate(
        item.dateResolvedByAgreement,
        DATE_FORMAT_LONG_FNS,
        SYSTEM_DATE_FORMAT_FNS,
      ),
      listId: item.guid || uuidv4(),
      isNew: !!item.isNew,
      organizationTemplate: createOrganizationTemplateSelectOption(resultDecisionsOrganization),
      organization: resultDecisionsOrganization,
      violatedMandatoryRequirements: item.violations
        ? convertKnmDataViolatedRequirementsToFormValues(item.violations)
        : undefined,
      act,
      signed: item.signed ?? undefined,
      paperless: item.paperless || undefined,
      extractVersion: item.extractVersion || undefined,
      familiarizationNotifications:
        item.familiarizationNotifications?.map(x => {
          const notification: IKnmActFamiliarizationNotificationsForm = {
            noticeDate: convertInitialKnmDateToFormDate(x.noticeDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
            knmNoticeType: x.knmNoticeType ? createKnmNotificationMethodSelectOption(x.knmNoticeType) : undefined,
            note: x.note,
            listId: x.guid || uuidv4(),
            isNew: !!x.isNew,
            existedAtSigning: x.existedAtSigning || undefined,
          };

          return notification;
        }) || undefined,
      firstExtractDateTime:
        convertInitialKnmDateToFormDate(item.firstExtractDateTime, DATE_FORMAT_LONG_MINUTE_FNS, DATE_FORMAT_ISO_FNS) ||
        null,
      systemSignatureUsed: item.systemSignatureUsed || false,
      systemSignatureTitle: item.systemSignatureTitle || null,
      systemSignatureMnemonic: item.systemSignatureMnemonic || null,
      systemSignatureDateTime: item.systemSignatureDateTime || null,
      cancelFioSigner: item.cancelFioSigner || undefined,
      cancelTitleSigner: item.cancelTitleSigner?.dictId
        ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
            state,
            item.cancelTitleSigner?.dictId,
            item.cancelTitleSigner?.dictVersionId,
          )
        : undefined,
      cancelComment: item.cancelComment || undefined,
      cancelDocument: item.cancelDocument || undefined,
      isCancelSigned: item.isCancelSigned || undefined,
      dateAdded: item.dateAdded || undefined,
      signers: convertSignersToFormValues(item.signers, state, undefined),
      prescriptionForUnidentifiedPersons:
        item.prescriptionForUnidentifiedPersons !== undefined
          ? sellersOptions.find(s =>
              item.prescriptionForUnidentifiedPersons === true ? s.value === true : s.value === false,
            )
          : undefined,
      productsDescription: normalizeProductsDescription(item.productsDescription) || undefined,
      validTo: convertInitialKnmDateToFormDate(item.validTo, DATE_FORMAT_LONG_FNS),
      isIndefinite: item.isIndefinite ?? undefined,
      productSalesResumption: item.productSalesResumption
        ? {
            resumptionDate: convertInitialKnmDateToFormDate(
              item.productSalesResumption.resumptionDate,
              DATE_FORMAT_LONG_FNS,
            ),
            decisionText: item.productSalesResumption.decisionText || undefined,
            documents: item.productSalesResumption.documents || undefined,
          }
        : undefined,
      prescriptionSigned: item.prescriptionSigned ?? undefined,
      resumptionSigned: item.resumptionSigned ?? undefined,
    };

    return formResultDecision;
  });

  if (promises) {
    const results = await Promise.all(promises);

    return results;
  }

  return undefined;
};
